@if (coach.active()) {
  @if (coach.minimized()) {
    <button
      #expandButton
      type="button"
      class="coach-minimized coach-expand"
      aria-expanded="false"
      [title]="'INSIGHT.COACH.EXPAND' | transloco"
      [formaAttention]="coach.minimizedAttention()"
      (attentionEnd)="coach.finishMinimizedAttention()"
      (click)="expand()">
      <span class="coach-avatar"><icon name="sparkle" [size]="24" [ariaHidden]="true" /></span>
      <span>
        {{ 'INSIGHT.COACH.NAME' | transloco }}
      </span>
    </button>
  } @else {
    <section
      #panel
      class="coach-panel"
      role="dialog"
      aria-modal="false"
      aria-labelledby="coach-title"
      [class.coach-panel--entering]="coach.origin() !== null"
      [class.coach-panel--maximized]="coach.maximized()"
      [class.coach-panel--right]="dockedRight()"
      [class.coach-panel--bottom]="dockedBottom()"
      [class.coach-panel--floating]="coach.layout() === 'floating'"
      [style]="panelStyle()"
      (keydown.escape)="onEscape($event)">
      @if (!coach.maximized()) {
        @if (!dockedToContent()) {
          <span
            class="coach-resize-handle coach-resize-handle--left"
            aria-hidden="true"
            [class.coach-resize-handle--active]="resizing()"
            (pointerdown)="startResize($event, 'left')"
            (pointermove)="resize($event)"
            (pointerup)="endResize()"
            (pointercancel)="endResize()"></span>
        }
        @if (!dockedRight() || dockedToContent()) {
          <span
            class="coach-resize-handle coach-resize-handle--right"
            aria-hidden="true"
            [class.coach-resize-handle--active]="resizing()"
            (pointerdown)="startResize($event, 'right')"
            (pointermove)="resize($event)"
            (pointerup)="endResize()"
            (pointercancel)="endResize()"></span>
        }
        @if (!dockedRight()) {
          @for (corner of resizeCorners(); track corner) {
            <span
              class="coach-resize-handle coach-resize-handle--corner coach-resize-handle--{{
                corner
              }}"
              aria-hidden="true"
              [class.coach-resize-handle--active]="resizing()"
              (pointerdown)="startResize($event, corner)"
              (pointermove)="resize($event)"
              (pointerup)="endResize()"
              (pointercancel)="endResize()"></span>
          }
          @for (edge of resizeEdges(); track edge) {
            <span
              class="coach-resize-handle coach-resize-handle--{{ edge }}"
              aria-hidden="true"
              [class.coach-resize-handle--active]="resizing()"
              (pointerdown)="startResize($event, edge)"
              (pointermove)="resize($event)"
              (pointerup)="endResize()"
              (pointercancel)="endResize()"></span>
          }
        }
      }
      @if (!docked()) {
        <span
          class="coach-drag-area"
          aria-hidden="true"
          [class.coach-header--dragging]="dragging()"
          (pointerdown)="startDrag($event)"
          (pointermove)="drag($event)"
          (pointerup)="endDrag()"
          (pointercancel)="endDrag()"></span>
      }
      <header
        #header
        class="coach-header"
        [class.coach-header--dragging]="dragging()"
        (pointerdown)="startDrag($event)"
        (pointermove)="drag($event)"
        (pointerup)="endDrag()"
        (pointercancel)="endDrag()">
        <span class="coach-avatar"><icon name="sparkle" [size]="24" [ariaHidden]="true" /></span>
        <h2 #title id="coach-title">
          {{ 'INSIGHT.COACH.TITLE' | transloco }}
          @if (currentGroup(); as group) {
            <span class="coach-group">
              <app-indicator-circle
                aria-hidden="true"
                [leaf]="group.leaf"
                [type]="group.category" />
              {{ group.name }}
            </span>
          }
        </h2>
        <forma-button-group class="coach-button-group" align="end">
          @if (compactHeader()) {
            <button
              #menuTrigger
              type="button"
              formaIconButton="ghost-small"
              icon="dots-three"
              class="coach-menu-trigger"
              [formaMenuTriggerFor]="coachMenu"
              [attr.aria-label]="'INSIGHT.COACH.MORE_ACTIONS' | transloco"
              [title]="'INSIGHT.COACH.MORE_ACTIONS' | transloco"></button>
            <ng-template #coachMenu>
              <forma-menu>
                @if (coach.messages().length && !confirmingReset()) {
                  <button
                    formaMenuItem
                    icon="note-pencil"
                    class="coach-new-conversation"
                    [disabled]="coach.busy()"
                    (triggered)="askReset()">
                    {{ 'INSIGHT.COACH.NEW_CONVERSATION' | transloco }}
                  </button>
                }
                <button
                  formaMenuItem
                  class="coach-maximize"
                  [icon]="coach.maximized() ? 'corners-in' : 'corners-out'"
                  (triggered)="coach.toggleMaximized()">
                  {{
                    (coach.maximized() ? 'INSIGHT.COACH.RESTORE' : 'INSIGHT.COACH.MAXIMIZE')
                      | transloco
                  }}
                </button>
                <button
                  formaMenuItem
                  icon="align-bottom-simple-bold"
                  class="coach-dock coach-dock-bottom"
                  (triggered)="dockBottom()">
                  {{ 'INSIGHT.COACH.DOCK_BOTTOM' | transloco }}
                </button>
                <button
                  formaMenuItem
                  icon="align-right-simple-bold"
                  class="coach-dock coach-dock-right"
                  (triggered)="dockRight()">
                  {{ 'INSIGHT.COACH.DOCK_RIGHT' | transloco }}
                </button>
              </forma-menu>
            </ng-template>
          } @else {
            @if (coach.messages().length && !confirmingReset()) {
              <button
                #newConversationButton
                type="button"
                formaIconButton="ghost-small"
                icon="note-pencil"
                class="coach-new-conversation"
                [disabled]="coach.busy()"
                [attr.aria-label]="'INSIGHT.COACH.NEW_CONVERSATION' | transloco"
                [title]="'INSIGHT.COACH.NEW_CONVERSATION' | transloco"
                (click)="askReset()"></button>
            }
            @let sizeLabel =
              (coach.maximized() ? 'INSIGHT.COACH.RESTORE' : 'INSIGHT.COACH.MAXIMIZE') | transloco;
            <button
              type="button"
              formaIconButton="ghost-small"
              class="coach-maximize"
              [icon]="coach.maximized() ? 'corners-in' : 'corners-out'"
              [attr.aria-label]="sizeLabel"
              [title]="sizeLabel"
              (click)="coach.toggleMaximized()"></button>
            <button
              type="button"
              formaIconButton="ghost-small"
              icon="align-bottom-simple-bold"
              class="coach-dock coach-dock-bottom"
              [class.coach-dock--active]="dockedBottom()"
              [attr.aria-pressed]="dockedBottom()"
              [attr.aria-label]="'INSIGHT.COACH.DOCK_BOTTOM' | transloco"
              [title]="'INSIGHT.COACH.DOCK_BOTTOM' | transloco"
              (click)="dockBottom()"></button>
            <button
              type="button"
              formaIconButton="ghost-small"
              icon="align-right-simple-bold"
              class="coach-dock coach-dock-right"
              [class.coach-dock--active]="dockedRight()"
              [attr.aria-pressed]="dockedRight()"
              [attr.aria-label]="'INSIGHT.COACH.DOCK_RIGHT' | transloco"
              [title]="'INSIGHT.COACH.DOCK_RIGHT' | transloco"
              (click)="dockRight()"></button>
          }
          <button
            type="button"
            formaIconButton="ghost-small"
            icon="minus"
            class="coach-minimize"
            [attr.aria-label]="'INSIGHT.COACH.MINIMIZE' | transloco"
            [title]="'INSIGHT.COACH.MINIMIZE' | transloco"
            (click)="minimize()"></button>
        </forma-button-group>
      </header>
      @if (confirmingReset()) {
        <div class="coach-reset" role="group" aria-labelledby="coach-reset-text">
          <p id="coach-reset-text">{{ 'INSIGHT.COACH.NEW_CONVERSATION_CONFIRM' | transloco }}</p>
          <div class="coach-reset-actions">
            <button formaButton="text" type="button" (click)="cancelReset()">
              {{ 'INSIGHT.COACH.NEW_CONVERSATION_CANCEL' | transloco }}
            </button>
            <button
              #confirmResetButton
              formaButton="primary"
              type="button"
              (click)="confirmReset()">
              {{ 'INSIGHT.COACH.NEW_CONVERSATION' | transloco }}
            </button>
          </div>
        </div>
      }
      @let proposal = coach.proposedAction();
      <app-agent-thread
        class="coach-thread"
        scrollShadow="both"
        scrollShadowAppearance="fade"
        [typewriter]="true"
        [messages]="coach.messages()"
        [busy]="coach.busy()"
        [pendingConfirmation]="coach.pendingConfirmation()"
        [confirmationApproveLabel]="'INSIGHT.COACH.CONFIRMATION.APPROVE' | transloco"
        [confirmationRejectLabel]="'INSIGHT.COACH.CONFIRMATION.REJECT' | transloco"
        [confirmationFeedbackLabel]="'INSIGHT.COACH.CONFIRMATION.FEEDBACK_LABEL' | transloco"
        [confirmationSaveLabel]="'INSIGHT.COACH.CONFIRMATION.SEND' | transloco"
        [confirmationCancelLabel]="'INSIGHT.COACH.CONFIRMATION.CANCEL' | transloco"
        [confirmationDetails]="
          proposal
            ? [
                {
                  label: 'INSIGHT.COACH.CONFIRMATION.ACTIVITY_TITLE' | transloco,
                  value: proposal.title,
                },
                {
                  label: 'INSIGHT.COACH.CONFIRMATION.ACTIVITY_DESCRIPTION' | transloco,
                  value: proposal.description,
                },
                {
                  label: 'INSIGHT.COACH.CONFIRMATION.ACTIVITY_AREA' | transloco,
                  value: proposal.areaName,
                },
                {
                  label: 'INSIGHT.COACH.CONFIRMATION.ACTIVITY_DEADLINE' | transloco,
                  value: proposal.deadline,
                },
              ]
            : []
        "
        (confirmationApproved)="coach.approve()"
        (confirmationRejected)="coach.reject($event)" />
      @if (coach.unavailable()) {
        <p class="coach-unavailable" role="alert">{{ 'INSIGHT.COACH.UNAVAILABLE' | transloco }}</p>
      }
      <app-agent-input-bar
        class="coach-composer"
        [attention]="composerAttention()"
        [attentionLoop]="coach.busy()"
        (attentionEnd)="composerAttention.set(null)"
        [autoFocus]="false"
        [sendDisabled]="coach.busy()"
        [value]="coach.draft()"
        (valueChange)="coach.setDraft($event)"
        [placeholder]="
          (coach.messages().length
            ? 'INSIGHT.COACH.REPLY_PLACEHOLDER'
            : 'INSIGHT.COACH.PLACEHOLDER'
          ) | transloco
        "
        [ariaLabel]="'INSIGHT.COACH.MESSAGE' | transloco"
        [sendLabel]="'INSIGHT.COACH.SEND' | transloco"
        (send)="coach.send($event)" />
    </section>
  }
}
